import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';
import routes from 'js/core_pre_webpack/routes';
import { Modal, ModalContent } from 'js/design_system/Modal';
import { Button } from 'js/design_system/Button';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { trackClick } from 'js/utils/trackClick';
import {
  recordShow,
  recordDismissal,
  recordClickthrough,
} from 'js/utils/dismissibles';

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-24);
  padding-top: 24px;
  max-width: 534px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  width: 100%;
  text-align: center;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  width: 100%;
`;

const FeaturesSection = styled.div`
  background-color: #f9f8f6;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 24px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 768px) {
    gap: 24px 41px;
    padding: 24px 82px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
`;

const FeatureIcon = styled.div`
  border-radius: 8px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureLabel = styled.span`
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  line-height: 20px;
  color: #535366;
  font-weight: 400;
  text-align: center;
  white-space: nowrap;
`;

const CTASection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  @media (max-width: 768px) {
    top: 12px;
    right: 12px;
    width: 32px;
    height: 32px;
  }
`;

const StyledHeading = styled.h1`
  font-family: 'DM Sans', sans-serif;
  font-size: 28px;
  line-height: 34px;
  margin: 0;
  color: #141413;
  font-weight: 400;
`;

const StyledBody = styled(Body)`
  font-size: 16px;
  line-height: 20px;
  color: #444340;
  font-weight: 400;
  margin: 0;
`;

const FeatureIconPodcast = styled(FeatureIcon)`
  background-color: #b9d5d7;
`;

const FeatureIconFiresideChat = styled(FeatureIcon)`
  background-color: #dabcb7;
`;

const FeatureIconAnalogy = styled(FeatureIcon)`
  background-color: #e2e0d9;
`;

const FeatureIconPost = styled(FeatureIcon)`
  background-color: #c7d5bc;
`;

const FeatureIconVideo = styled(FeatureIcon)`
  background-color: #c0bedc;
`;

const FeatureIconComic = styled(FeatureIcon)`
  background-color: #e9e2c4;
`;

const FeatureIconSlide = styled(FeatureIcon)`
  background-color: #e2e0d9;
`;

const StyledButton = styled(Button)`
  background-color: #0645b1;
  color: #ffffff;
  font-family: 'DM Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 20px;
  width: 100%;
  border-radius: 8px;
  border: none;
  padding: 22px 24px;
  min-height: 64px;
  height: 64px;
`;

interface ImpactHubModalProps {
  open?: boolean;
  onClose?: () => void;
}

const DISMISSIBLE_NAME = 'ImpactHubModal';

const ImpactHubModal: React.FC<ImpactHubModalProps> = ({
  open: openProp = true,
  onClose,
}) => {
  const [isOpen, setIsOpen] = useState(openProp);

  useEffect(() => {
    if (openProp) {
      recordShow(DISMISSIBLE_NAME);
    }
  }, [openProp]);

  // Explicitly clean up body overflow when modal closes
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  const handleClose = async () => {
    // Record dismissal
    try {
      await recordDismissal(DISMISSIBLE_NAME);
    } catch (_error) {
      // Silently handle errors - modal should still close
    }

    // Close the modal by updating state
    setIsOpen(false);

    // Call onClose callback if provided
    if (onClose) {
      onClose();
    }
  };

  const handleCTAClick = () => {
    // Track the click
    trackClick({
      source: 'impact_hub_modal_cta',
      extra: { action: 'explore_impact_hub' },
    });

    // Record clickthrough for dismissible
    recordClickthrough(DISMISSIBLE_NAME, { action: 'explore_impact_hub' });

    // Navigate to Impact Hub
    window.location.href = routes.impact_home_url();
  };

  return (
    <Modal open={isOpen} onClose={handleClose} showHeader={false}>
      <ModalContent>
        <CloseButton onClick={handleClose} aria-label="Close">
          <Icon name="close" size={24} />
        </CloseButton>
        <ContentContainer>
          <HeaderSection>
            <HeaderText>
              <StyledHeading>Amplify your research impact</StyledHeading>

              <StyledBody size="md">
                AI transforms your papers into content that spreads
              </StyledBody>
            </HeaderText>

            <FeaturesSection>
              <FeatureItem>
                <FeatureIconPodcast>
                  <Icon name="mic" size={20} />
                </FeatureIconPodcast>
                <FeatureLabel>Podcast</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureIconFiresideChat>
                  <Icon name="adaptive_audio_mic" size={20} />
                </FeatureIconFiresideChat>
                <FeatureLabel>Fireside chat</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureIconAnalogy>
                  <Icon name="lightbulb" size={20} />
                </FeatureIconAnalogy>
                <FeatureLabel>Analogy</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureIconPost>
                  <Icon name="campaign" size={20} />
                </FeatureIconPost>
                <FeatureLabel>Post</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureIconVideo>
                  <Icon name="video_library" size={20} />
                </FeatureIconVideo>
                <FeatureLabel>Video</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureIconComic>
                  <Icon name="comic_bubble" size={20} />
                </FeatureIconComic>
                <FeatureLabel>Comic</FeatureLabel>
              </FeatureItem>

              <FeatureItem>
                <FeatureIconSlide>
                  <Icon name="web_stories" size={20} />
                </FeatureIconSlide>
                <FeatureLabel>Slides</FeatureLabel>
              </FeatureItem>
            </FeaturesSection>
          </HeaderSection>

          <CTASection>
            <StyledButton
              size="lg"
              iconRight="arrow_forward"
              onClick={handleCTAClick}
            >
              Explore Impact Hub
            </StyledButton>
          </CTASection>
        </ContentContainer>
      </ModalContent>
    </Modal>
  );
};

export default ImpactHubModal;
